/**
 * Utility helper to ensure high quality, professional document vector mockup images for products.
 * Automatically generates 100% accurate, high-resolution vector previews tailored to each
 * document title (Passports, Employment Authorization Cards, Driving Licenses, Green Cards, SSN,
 * Utility Bills, Bank Statements, ID Cards, Tax Returns, Certificates, etc.).
 */

function hashCode(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash;
}

function cleanTitleForDisplay(title?: string): string {
  if (!title) return 'DIGITAL VECTOR TEMPLATE';
  return title
    .replace(/\b(psd|ai|cdr|pdf|vector|template|editable|design|download|old version|new version|latest version|victor design)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function generateDynamicProductSvg(
  product?: { title?: string; category?: string; country?: string; formats?: string[] } | null,
  viewType: 'front' | 'back' = 'front'
): string {
  const rawTitle = product?.title || 'Digital Vector Template';
  const category = product?.category || 'Document';
  const country = product?.country || 'USA';
  const formats = Array.isArray(product?.formats) && product.formats.length > 0 ? product.formats.join(' .') : 'PSD .AI';

  const cleanTitle = cleanTitleForDisplay(rawTitle);
  const displayTitleUpper = escapeXml(cleanTitle.toUpperCase());
  const titleLower = rawTitle.toLowerCase();
  const catLower = category.toLowerCase();
  const countryUpper = escapeXml((country || 'USA').toUpperCase());

  const seed = Math.abs(hashCode(rawTitle));
  const docNumber = (seed % 899999) + 100000;
  const uscisNumber = `${(seed % 899) + 100}-${((seed * 3) % 8999) + 1000}`;

  // Common SVG Defs & Patterns
  const defs = `
    <defs>
      <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#0b1329"/>
        <stop offset="50%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#030712"/>
      </linearGradient>
      <linearGradient id="cardGradGreen" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#064e3b"/>
        <stop offset="50%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#022c22"/>
      </linearGradient>
      <linearGradient id="cardGradBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#0369a1"/>
        <stop offset="50%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#1e1b4b"/>
      </linearGradient>
      <linearGradient id="cardGradNavy" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#1e1b4b"/>
        <stop offset="50%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
      <linearGradient id="cardGradGold" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#eab308"/>
        <stop offset="100%" stop-color="#ca8a04"/>
      </linearGradient>
      <linearGradient id="goldText" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="100%" stop-color="#eab308"/>
      </linearGradient>
      <pattern id="guilloche" width="30" height="30" patternUnits="userSpaceOnUse">
        <path d="M 0 15 Q 7.5 0 15 15 T 30 15" fill="none" stroke="#38bdf8" stroke-width="0.4" opacity="0.18"/>
        <path d="M 0 15 Q 7.5 30 15 15 T 30 15" fill="none" stroke="#0284c7" stroke-width="0.4" opacity="0.18"/>
      </pattern>
      <pattern id="dotGrid" width="16" height="16" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1" fill="#38bdf8" opacity="0.15"/>
      </pattern>
    </defs>
  `;

  // 1. EMPLOYMENT AUTHORIZATION / WORK PERMIT / GREEN CARD / RESIDENT CARD
  if (
    titleLower.includes('employment authorization') ||
    titleLower.includes('work permit') ||
    titleLower.includes('green card') ||
    titleLower.includes('permanent resident') ||
    catLower.includes('authorization') ||
    titleLower.includes('marijuana')
  ) {
    const isGreen = titleLower.includes('green card') || titleLower.includes('permanent resident');
    const isMarijuana = titleLower.includes('marijuana');
    const headerTitle = isGreen
      ? 'PERMANENT RESIDENT CARD'
      : isMarijuana
      ? 'MEDICAL MARIJUANA IDENTIFICATION CARD'
      : 'EMPLOYMENT AUTHORIZATION CARD';
    const subTitleText = isGreen
      ? `${countryUpper} DEPARTMENT OF HOMELAND SECURITY`
      : isMarijuana
      ? `${countryUpper} HEALTH & HUMAN SERVICES REGISTRY`
      : `${countryUpper} U.S. CITIZENSHIP & IMMIGRATION SERVICES`;

    if (viewType === 'back') {
      return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
        ${defs}
        <rect width="800" height="500" fill="url(#bgGrad)"/>
        <rect x="40" y="30" width="720" height="440" rx="20" fill="url(#cardGradGreen)" stroke="#10b981" stroke-width="2"/>
        <rect x="40" y="30" width="720" height="440" rx="20" fill="url(#guilloche)"/>
        <rect x="70" y="60" width="660" height="50" rx="8" fill="#064e3b" stroke="#10b981" stroke-width="1"/>
        <text x="90" y="92" font-family="Arial, sans-serif" font-weight="900" font-size="16" fill="#6ee7b7" letter-spacing="2">REVERSE SIDE SPECIFICATIONS &amp; OPTICAL STRIPE</text>
        <rect x="70" y="130" width="660" height="70" rx="4" fill="#0f172a" stroke="#334155"/>
        <path d="M 90 145 h 620 M 90 165 h 620 M 90 185 h 620" stroke="#10b981" stroke-width="8" stroke-dasharray="20,10" opacity="0.6"/>
        <text x="80" y="235" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af">Card Number: <tspan font-weight="bold" fill="#ffffff">EAD-${docNumber}-US</tspan></text>
        <text x="80" y="260" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af">USCIS #: <tspan font-weight="bold" fill="#6ee7b7">${uscisNumber}</tspan></text>
        <rect x="70" y="300" width="660" height="130" rx="12" fill="#062c22" stroke="#059669"/>
        <text x="90" y="335" font-family="monospace" font-size="16" fill="#34d399" letter-spacing="4">C1USA${uscisNumber}&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</text>
        <text x="90" y="365" font-family="monospace" font-size="16" fill="#34d399" letter-spacing="4">9501018M2910015USA&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;08</text>
        <text x="90" y="395" font-family="monospace" font-size="16" fill="#34d399" letter-spacing="4">SPECIMEN&lt;&lt;RH&lt;EDITOGRAPHY&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</text>
      </svg>
      `)}`;
    }

    return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
      ${defs}
      <rect width="800" height="500" fill="url(#bgGrad)"/>
      <rect x="40" y="30" width="720" height="440" rx="22" fill="url(#cardGradGreen)" stroke="#10b981" stroke-width="2.5"/>
      <rect x="40" y="30" width="720" height="440" rx="22" fill="url(#guilloche)"/>
      <!-- Header Bar -->
      <rect x="40" y="30" width="720" height="85" rx="22" fill="#064e3b"/>
      <rect x="40" y="85" width="720" height="30" fill="#047857"/>
      <text x="70" y="70" font-family="Arial, sans-serif" font-weight="900" font-size="22" fill="#ffffff" letter-spacing="2">${headerTitle}</text>
      <text x="70" y="105" font-family="Arial, sans-serif" font-weight="bold" font-size="12" fill="#a7f3d0">${subTitleText}</text>
      <rect x="610" y="48" width="130" height="32" rx="8" fill="#10b981"/>
      <text x="675" y="69" font-family="Arial, sans-serif" font-weight="black" font-size="12" fill="#064e3b" text-anchor="middle">EDITABLE .${formats}</text>
      <!-- Main Product Title Display -->
      <rect x="70" y="130" width="660" height="45" rx="10" fill="#022c22" stroke="#10b981" stroke-width="1"/>
      <text x="85" y="158" font-family="Arial, sans-serif" font-weight="900" font-size="16" fill="#34d399" letter-spacing="1">${displayTitleUpper}</text>
      <!-- Photo Slot -->
      <rect x="70" y="190" width="160" height="200" rx="14" fill="#022c22" stroke="#34d399" stroke-width="2"/>
      <circle cx="150" cy="260" r="42" fill="#064e3b"/>
      <path d="M 105 350 Q 150 290 195 350" fill="#064e3b"/>
      <rect x="70" y="190" width="160" height="200" rx="14" fill="url(#guilloche)" opacity="0.4"/>
      <text x="150" y="375" font-family="Arial, sans-serif" font-size="10" fill="#6ee7b7" font-weight="bold" text-anchor="middle">SMART PHOTO LAYER</text>
      <!-- Information Fields -->
      <text x="250" y="210" font-family="Arial, sans-serif" font-size="11" fill="#6ee7b7" font-weight="bold">CATEGORY / CLASS</text>
      <text x="250" y="232" font-family="Arial, sans-serif" font-size="18" fill="#ffffff" font-weight="black">C03 / C08 (WORK PERMIT)</text>
      <text x="250" y="265" font-family="Arial, sans-serif" font-size="11" fill="#6ee7b7" font-weight="bold">USCIS / REGISTRATION NO.</text>
      <text x="250" y="288" font-family="monospace" font-size="20" fill="#34d399" font-weight="black">${uscisNumber}</text>
      <text x="250" y="320" font-family="Arial, sans-serif" font-size="11" fill="#6ee7b7" font-weight="bold">CARD # / SPECIMEN</text>
      <text x="250" y="342" font-family="Arial, sans-serif" font-size="15" fill="#f8fafc" font-weight="bold">EAD-${docNumber}-RH</text>
      <text x="250" y="372" font-family="Arial, sans-serif" font-size="11" fill="#6ee7b7" font-weight="bold">VALIDITY DATES</text>
      <text x="250" y="392" font-family="Arial, sans-serif" font-size="14" fill="#ffffff" font-weight="bold">10 JAN 2024 TO 10 JAN 2029</text>
      <!-- Watermark Badge -->
      <rect x="70" y="405" width="660" height="45" rx="10" fill="#0f172a" stroke="#1e293b"/>
      <text x="400" y="432" font-family="Arial, sans-serif" font-size="12" fill="#10b981" font-weight="black" text-anchor="middle" letter-spacing="3">RH EDITOGRAPHY — 100% EDITABLE PSD TEMPLATE</text>
    </svg>
    `)}`;
  }

  // 2. PASSPORT
  if (titleLower.includes('passport') || catLower.includes('passport')) {
    if (viewType === 'back') {
      return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
        ${defs}
        <rect width="800" height="500" fill="url(#bgGrad)"/>
        <rect x="40" y="30" width="720" height="440" rx="20" fill="url(#cardGradNavy)" stroke="url(#goldText)" stroke-width="2"/>
        <rect x="70" y="60" width="660" height="380" rx="16" fill="#0f172a" stroke="#1e293b"/>
        <text x="400" y="120" font-family="Arial, sans-serif" font-weight="900" font-size="22" fill="url(#goldText)" text-anchor="middle" letter-spacing="3">${countryUpper} PASSPORT INSIDE COVER</text>
        <circle cx="400" cy="220" r="50" fill="none" stroke="url(#cardGradGold)" stroke-width="3"/>
        <text x="400" y="225" font-family="Arial, sans-serif" font-size="12" fill="#eab308" font-weight="bold" text-anchor="middle">OFFICIAL CREST</text>
        <text x="400" y="320" font-family="Arial, sans-serif" font-size="14" fill="#38bdf8" text-anchor="middle" font-weight="bold">${displayTitleUpper}</text>
        <text x="400" y="360" font-family="monospace" font-size="16" fill="#eab308" text-anchor="middle" letter-spacing="2">3000 x 2000 PX — HIGH RESOLUTION SMART LAYERS</text>
      </svg>
      `)}`;
    }

    return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
      ${defs}
      <rect width="800" height="500" fill="url(#bgGrad)"/>
      <rect x="40" y="30" width="720" height="440" rx="20" fill="url(#cardGradNavy)" stroke="url(#goldText)" stroke-width="2"/>
      <rect x="40" y="30" width="720" height="440" rx="20" fill="url(#guilloche)"/>
      <!-- Passport Header -->
      <rect x="70" y="50" width="660" height="60" rx="12" fill="#0284c7" opacity="0.25"/>
      <text x="90" y="88" font-family="Arial, sans-serif" font-weight="900" font-size="22" fill="#38bdf8" letter-spacing="3">PASSPORT / PASSEPORT</text>
      <text x="690" y="88" font-family="monospace" font-weight="bold" font-size="14" fill="#eab308" text-anchor="end">.${formats}</text>
      <!-- Title Bar -->
      <rect x="70" y="120" width="660" height="35" rx="6" fill="#0f172a" stroke="#0284c7"/>
      <text x="85" y="143" font-family="Arial, sans-serif" font-weight="bold" font-size="14" fill="#fef08a">${displayTitleUpper}</text>
      <!-- Photo Slot -->
      <rect x="80" y="170" width="160" height="180" rx="12" fill="#090d16" stroke="#38bdf8" stroke-width="1.5"/>
      <circle cx="160" cy="235" r="40" fill="#1e293b"/>
      <path d="M 120 325 Q 160 285 200 325" fill="#1e293b"/>
      <rect x="80" y="170" width="160" height="180" rx="12" fill="url(#guilloche)" opacity="0.5"/>
      <text x="160" y="340" font-family="Arial, sans-serif" font-size="10" fill="#0284c7" font-weight="bold" text-anchor="middle">PASSPORT PHOTO</text>
      <!-- Fields -->
      <text x="270" y="190" font-family="Arial, sans-serif" font-size="10" fill="#64748b" font-weight="bold">SURNAME / NOM</text>
      <text x="270" y="210" font-family="Arial, sans-serif" font-size="16" fill="#ffffff" font-weight="bold">SPECIMEN / VECTOR TEMPLATE</text>
      <text x="270" y="240" font-family="Arial, sans-serif" font-size="10" fill="#64748b" font-weight="bold">NATIONALITY / NATIONALITE</text>
      <text x="270" y="260" font-family="Arial, sans-serif" font-size="15" fill="#38bdf8" font-weight="bold">${countryUpper}</text>
      <text x="520" y="240" font-family="Arial, sans-serif" font-size="10" fill="#64748b" font-weight="bold">PASSPORT NO.</text>
      <text x="520" y="260" font-family="monospace" font-size="16" fill="#fef08a" font-weight="bold">P${docNumber}US</text>
      <text x="270" y="290" font-family="Arial, sans-serif" font-size="10" fill="#64748b" font-weight="bold">DATE OF BIRTH / EXPIRATION</text>
      <text x="270" y="310" font-family="Arial, sans-serif" font-size="14" fill="#f8fafc" font-weight="bold">01 JAN 1995 / 01 JAN 2035</text>
      <!-- MRZ Zone -->
      <rect x="70" y="360" width="660" height="90" rx="10" fill="#090d16" stroke="#1e293b"/>
      <text x="90" y="395" font-family="monospace" font-size="16" fill="#38bdf8" letter-spacing="4">P&lt;${countryUpper.slice(0,3)}SPECIMEN&lt;&lt;RH&lt;EDITOGRAPHY&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</text>
      <text x="90" y="425" font-family="monospace" font-size="16" fill="#38bdf8" letter-spacing="4">${docNumber}0USA9501018M3001011&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;06</text>
    </svg>
    `)}`;
  }

  // 3. UTILITY BILL / ENERGY STATEMENT
  if (titleLower.includes('bill') || titleLower.includes('utility') || catLower.includes('bill')) {
    return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
      ${defs}
      <rect width="800" height="500" fill="url(#bgGrad)"/>
      <rect x="50" y="20" width="700" height="460" rx="16" fill="#ffffff" stroke="#cbd5e1" stroke-width="2"/>
      <!-- Header -->
      <rect x="50" y="20" width="700" height="80" rx="16" fill="#0f172a"/>
      <text x="80" y="58" font-family="Arial, sans-serif" font-weight="900" font-size="20" fill="#38bdf8">UTILITY STATEMENT &amp; PROOF OF ADDRESS</text>
      <text x="80" y="80" font-family="Arial, sans-serif" font-size="12" fill="#94a3b8">${countryUpper} ACCOUNT INVOICE TEMPLATE</text>
      <rect x="580" y="40" width="140" height="38" rx="8" fill="#0284c7"/>
      <text x="650" y="64" font-family="Arial, sans-serif" font-weight="bold" font-size="12" fill="#ffffff" text-anchor="middle">EDITABLE .${formats}</text>
      <!-- Title Box -->
      <rect x="80" y="115" width="640" height="38" rx="8" fill="#f1f5f9" stroke="#cbd5e1"/>
      <text x="95" y="139" font-family="Arial, sans-serif" font-weight="bold" font-size="14" fill="#0f172a">${displayTitleUpper}</text>
      <!-- Account Info -->
      <rect x="80" y="165" width="310" height="110" rx="8" fill="#f8fafc" stroke="#cbd5e1"/>
      <text x="95" y="188" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#64748b">ACCOUNT HOLDER</text>
      <text x="95" y="210" font-family="Arial, sans-serif" font-size="15" font-weight="bold" fill="#0f172a">JOHN SPECIMEN CITIZEN</text>
      <text x="95" y="230" font-family="Arial, sans-serif" font-size="12" fill="#334155">123 VICTORIA STREET, SUITE 400, ${countryUpper}</text>
      <!-- Summary Box -->
      <rect x="410" y="165" width="310" height="110" rx="8" fill="#f0f9ff" stroke="#bae6fd"/>
      <text x="425" y="188" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#0369a1">STATEMENT SUMMARY</text>
      <text x="425" y="210" font-family="Arial, sans-serif" font-size="12" fill="#0f172a">Account No: <tspan font-weight="bold">${docNumber}-2024</tspan></text>
      <text x="425" y="250" font-family="Arial, sans-serif" font-size="18" font-weight="black" fill="#0284c7">TOTAL DUE: $148.50</text>
      <!-- Usage Table -->
      <rect x="80" y="290" width="640" height="110" rx="8" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="80" y="290" width="640" height="30" rx="8" fill="#f1f5f9"/>
      <text x="100" y="310" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#334155">SERVICE DETAILS</text>
      <text x="600" y="310" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#334155" text-anchor="end">AMOUNT ($)</text>
      <text x="100" y="340" font-family="Arial, sans-serif" font-size="13" fill="#0f172a">Monthly Service Charge</text>
      <text x="600" y="340" font-family="Arial, sans-serif" font-size="13" font-weight="bold" fill="#0f172a" text-anchor="end">$148.50</text>
      <!-- Footer -->
      <text x="80" y="445" font-family="Arial, sans-serif" font-size="11" fill="#64748b" font-weight="bold">OFFICIAL PROOF OF ADDRESS VECTOR TEMPLATE — RH EDITOGRAPHY</text>
    </svg>
    `)}`;
  }

  // 4. BANK STATEMENT
  if (titleLower.includes('statement') || titleLower.includes('bank') || catLower.includes('statement')) {
    return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
      ${defs}
      <rect width="800" height="500" fill="url(#bgGrad)"/>
      <rect x="50" y="20" width="700" height="460" rx="16" fill="#ffffff" stroke="#cbd5e1" stroke-width="2"/>
      <!-- Header -->
      <rect x="50" y="20" width="700" height="85" rx="16" fill="#0f172a"/>
      <circle cx="95" cy="62" r="22" fill="#16a34a"/>
      <text x="95" y="69" font-family="Arial, sans-serif" font-weight="900" font-size="18" fill="#ffffff" text-anchor="middle">RH</text>
      <text x="135" y="58" font-family="Arial, sans-serif" font-weight="900" font-size="20" fill="#ffffff">GLOBAL CAPITAL BANK</text>
      <text x="135" y="78" font-family="Arial, sans-serif" font-size="12" fill="#38bdf8">OFFICIAL FINANCIAL STATEMENT TEMPLATE — ${countryUpper}</text>
      <rect x="560" y="42" width="160" height="40" rx="8" fill="#16a34a"/>
      <text x="640" y="67" font-family="Arial, sans-serif" font-weight="bold" font-size="12" fill="#ffffff" text-anchor="middle">AUTO-CALC .${formats}</text>
      <!-- Title -->
      <rect x="80" y="120" width="640" height="35" rx="8" fill="#f8fafc" stroke="#cbd5e1"/>
      <text x="95" y="143" font-family="Arial, sans-serif" font-weight="bold" font-size="14" fill="#0f172a">${displayTitleUpper}</text>
      <!-- Summary Cards -->
      <rect x="80" y="170" width="200" height="75" rx="10" fill="#f8fafc" stroke="#e2e8f0"/>
      <text x="95" y="195" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="#64748b">STARTING BALANCE</text>
      <text x="95" y="222" font-family="Arial, sans-serif" font-size="18" font-weight="black" fill="#0f172a">$12,450.00</text>
      <rect x="300" y="170" width="200" height="75" rx="10" fill="#f0fdf4" stroke="#bbf7d0"/>
      <text x="315" y="195" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="#166534">TOTAL DEPOSITS</text>
      <text x="315" y="222" font-family="Arial, sans-serif" font-size="18" font-weight="black" fill="#15803d">+$8,900.50</text>
      <rect x="520" y="170" width="200" height="75" rx="10" fill="#f0f9ff" stroke="#bae6fd"/>
      <text x="535" y="195" font-family="Arial, sans-serif" font-size="10" font-weight="bold" fill="#0369a1">ENDING BALANCE</text>
      <text x="535" y="222" font-family="Arial, sans-serif" font-size="18" font-weight="black" fill="#0284c7">$21,350.50</text>
      <!-- Table -->
      <rect x="80" y="260" width="640" height="130" rx="10" fill="#ffffff" stroke="#e2e8f0"/>
      <rect x="80" y="260" width="640" height="30" rx="10" fill="#f1f5f9"/>
      <text x="100" y="280" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#475569">DATE / DESCRIPTION</text>
      <text x="690" y="280" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#475569" text-anchor="end">AMOUNT</text>
      <text x="100" y="312" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#0f172a">PAYROLL DIRECT DEPOSIT</text>
      <text x="690" y="312" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#16a34a" text-anchor="end">+$4,500.00</text>
      <text x="100" y="345" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#0f172a">INTERNATIONAL WIRE TRANSFER</text>
      <text x="690" y="345" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="#16a34a" text-anchor="end">+$3,200.00</text>
      <!-- Stamp -->
      <text x="80" y="445" font-family="Arial, sans-serif" font-size="11" fill="#64748b" font-weight="bold">100% EDITABLE VECTOR BANK STATEMENT TEMPLATE — RH EDITOGRAPHY</text>
    </svg>
    `)}`;
  }

  // 5. DRIVING LICENSE / ID CARD / IDENTITY CARD
  // Matches: Driving License, Driver's License, ID Card, Medical Card, State ID, etc.
  if (viewType === 'back') {
    return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
      ${defs}
      <rect width="800" height="500" fill="url(#bgGrad)"/>
      <rect x="40" y="30" width="720" height="440" rx="24" fill="url(#cardGradBlue)" stroke="#38bdf8" stroke-width="2"/>
      <rect x="40" y="30" width="720" height="440" rx="24" fill="url(#guilloche)"/>
      <rect x="40" y="70" width="720" height="60" fill="#090d16"/>
      <text x="70" y="105" font-family="Arial, sans-serif" font-size="12" fill="#38bdf8" font-weight="bold">MAGNETIC STRIPE &amp; REVERSE SIDE SECURITY ZONE</text>
      <rect x="70" y="160" width="660" height="120" rx="8" fill="#ffffff"/>
      <path d="M 90 170 h 8 v 100 h -8 Z M 105 170 h 15 v 100 h -15 Z M 130 170 h 4 v 100 h -4 Z M 140 170 h 10 v 100 h -10 Z M 160 170 h 20 v 100 h -20 Z M 190 170 h 6 v 100 h -6 Z M 205 170 h 12 v 100 h -12 Z M 225 170 h 8 v 100 h -8 Z M 240 170 h 15 v 100 h -15 Z M 265 170 h 5 v 100 h -5 Z M 280 170 h 18 v 100 h -18 Z M 305 170 h 7 v 100 h -7 Z M 320 170 h 14 v 100 h -14 Z M 340 170 h 6 v 100 h -6 Z M 355 170 h 10 v 100 h -10 Z M 375 170 h 16 v 100 h -16 Z M 400 170 h 5 v 100 h -5 Z M 415 170 h 12 v 100 h -12 Z M 435 170 h 8 v 100 h -8 Z M 450 170 h 20 v 100 h -20 Z M 480 170 h 6 v 100 h -6 Z M 495 170 h 14 v 100 h -14 Z M 515 170 h 8 v 100 h -8 Z M 530 170 h 10 v 100 h -10 Z M 550 170 h 15 v 100 h -15 Z M 575 170 h 6 v 100 h -6 Z M 590 170 h 12 v 100 h -12 Z M 610 170 h 8 v 100 h -8 Z M 625 170 h 18 v 100 h -18 Z M 650 170 h 5 v 100 h -5 Z M 665 170 h 12 v 100 h -12 Z" fill="#000000"/>
      <text x="70" y="320" font-family="Arial, sans-serif" font-size="12" fill="#94a3b8">Document Number: <tspan font-weight="bold" fill="#ffffff">DL-${docNumber}-X9</tspan></text>
      <text x="70" y="350" font-family="Arial, sans-serif" font-size="12" fill="#94a3b8">Jurisdiction: <tspan font-weight="bold" fill="#38bdf8">${countryUpper}</tspan></text>
      <text x="70" y="410" font-family="Arial, sans-serif" font-size="12" fill="#e2e8f0" font-weight="bold">${displayTitleUpper}</text>
    </svg>
    `)}`;
  }

  return `data:image/svg+xml,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 500" width="800" height="500">
    ${defs}
    <rect width="800" height="500" fill="url(#bgGrad)"/>
    <rect x="40" y="30" width="720" height="440" rx="24" fill="url(#cardGradBlue)" stroke="#38bdf8" stroke-width="2"/>
    <rect x="40" y="30" width="720" height="440" rx="24" fill="url(#guilloche)"/>
    <!-- Header Bar -->
    <rect x="40" y="30" width="720" height="80" rx="24" fill="#0369a1"/>
    <rect x="40" y="80" width="720" height="30" fill="#0284c7"/>
    <text x="70" y="68" font-family="Arial, sans-serif" font-weight="900" font-size="22" fill="#ffffff" letter-spacing="2">${escapeXml((category || 'IDENTIFICATION').toUpperCase())} TEMPLATE</text>
    <text x="70" y="100" font-family="Arial, sans-serif" font-weight="bold" font-size="12" fill="#e0f2fe">${countryUpper} STANDARD EDITABLE VECTOR FORMAT</text>
    <rect x="610" y="48" width="130" height="32" rx="8" fill="#38bdf8"/>
    <text x="675" y="69" font-family="Arial, sans-serif" font-weight="black" font-size="12" fill="#0369a1" text-anchor="middle">.${formats}</text>
    <!-- Title Bar -->
    <rect x="70" y="125" width="660" height="42" rx="8" fill="#090d16" stroke="#0284c7"/>
    <text x="85" y="152" font-family="Arial, sans-serif" font-weight="900" font-size="15" fill="#38bdf8" letter-spacing="1">${displayTitleUpper}</text>
    <!-- Photo Slot -->
    <rect x="70" y="180" width="165" height="205" rx="14" fill="#0f172a" stroke="#38bdf8" stroke-width="2"/>
    <circle cx="152" cy="250" r="42" fill="#334155"/>
    <path d="M 107 340 Q 152 290 197 340" fill="#334155"/>
    <rect x="70" y="180" width="165" height="205" rx="14" fill="url(#guilloche)" opacity="0.4"/>
    <text x="152" y="368" font-family="Arial, sans-serif" font-size="10" fill="#38bdf8" font-weight="bold" text-anchor="middle">SMART PHOTO LAYER</text>
    <!-- Fields -->
    <text x="260" y="200" font-family="Arial, sans-serif" font-size="11" fill="#94a3b8" font-weight="bold">1. LN: SPECIMEN</text>
    <text x="260" y="222" font-family="Arial, sans-serif" font-size="18" fill="#ffffff" font-weight="black">2. FN: DRIVER / CARDHOLDER</text>
    <text x="260" y="255" font-family="Arial, sans-serif" font-size="12" fill="#94a3b8" font-weight="bold">3. DOB: 12.05.1992   4a. ISS: 10.01.2024</text>
    <text x="260" y="278" font-family="Arial, sans-serif" font-size="12" fill="#94a3b8" font-weight="bold">4b. EXP: 10.01.2034   4c. AUTH: RH EDITOGRAPHY</text>
    <text x="260" y="315" font-family="Arial, sans-serif" font-size="11" fill="#94a3b8" font-weight="bold">5. DOCUMENT NO.</text>
    <text x="260" y="342" font-family="monospace" font-size="20" fill="#38bdf8" font-weight="black">DL-${docNumber}-X9</text>
    <!-- Barcode -->
    <rect x="70" y="398" width="660" height="52" rx="8" fill="#ffffff"/>
    <path d="M 90 406 h 5 v 36 h -5 Z M 100 406 h 10 v 36 h -10 Z M 115 406 h 3 v 36 h -3 Z M 122 406 h 8 v 36 h -8 Z M 135 406 h 12 v 36 h -12 Z M 152 406 h 4 v 36 h -4 Z M 160 406 h 15 v 36 h -15 Z M 180 406 h 6 v 36 h -6 Z M 190 406 h 10 v 36 h -10 Z M 205 406 h 4 v 36 h -4 Z M 215 406 h 12 v 36 h -12 Z M 232 406 h 6 v 36 h -6 Z M 242 406 h 14 v 36 h -14 Z M 260 406 h 5 v 36 h -5 Z M 270 406 h 10 v 36 h -10 Z M 285 406 h 8 v 36 h -8 Z M 300 406 h 12 v 36 h -12 Z M 315 406 h 4 v 36 h -4 Z M 325 406 h 15 v 36 h -15 Z M 345 406 h 6 v 36 h -6 Z M 355 406 h 10 v 36 h -10 Z M 370 406 h 8 v 36 h -8 Z M 382 406 h 12 v 36 h -12 Z M 400 406 h 5 v 36 h -5 Z M 410 406 h 14 v 36 h -14 Z M 430 406 h 6 v 36 h -6 Z M 440 406 h 10 v 36 h -10 Z M 455 406 h 8 v 36 h -8 Z M 470 406 h 12 v 36 h -12 Z M 485 406 h 4 v 36 h -4 Z M 495 406 h 15 v 36 h -15 Z M 515 406 h 6 v 36 h -6 Z M 525 406 h 10 v 36 h -10 Z M 540 406 h 8 v 36 h -8 Z M 552 406 h 12 v 36 h -12 Z M 570 406 h 5 v 36 h -5 Z M 580 406 h 14 v 36 h -14 Z M 600 406 h 6 v 36 h -6 Z M 610 406 h 10 v 36 h -10 Z M 625 406 h 8 v 36 h -8 Z M 640 406 h 12 v 36 h -12 Z M 655 406 h 4 v 36 h -4 Z M 665 406 h 15 v 36 h -15 Z M 685 406 h 6 v 36 h -6 Z" fill="#000000"/>
  </svg>
  `)}`;
}

export function normalizeProductImageUrl(img: string): string {
  if (!img || typeof img !== 'string') return '';
  const trimmed = img.trim();
  if (trimmed.length === 0) return '';
  if (trimmed.startsWith('data:') || trimmed.startsWith('/')) return trimmed;

  // Direct high-speed Firebase Storage & Google Cloud Storage CDN
  if (trimmed.includes('firebasestorage.googleapis.com') || trimmed.includes('storage.googleapis.com')) {
    return trimmed;
  }

  // Google Drive preview links normalized to reliable high-res direct image thumbnails
  if (trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com')) {
    const match = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/) || trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (match && match[1]) {
      return `https://lh3.googleusercontent.com/d/${match[1]}`;
    }
  }

  return trimmed;
}

/**
 * Generates a canonical deduplication key for product image references,
 * ensuring that identical images (with varying query strings, tokens or proxy wrappers)
 * are never displayed multiple times.
 */
export function getImageDeduplicationKey(url: string): string {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // 1. Firebase Storage URLs: match the object path: /o/(public%2F...)?
  if (trimmed.includes('firebasestorage.googleapis.com')) {
    const match = trimmed.match(/\/o\/([^?#]+)/);
    if (match && match[1]) {
      return `fb:${decodeURIComponent(match[1])}`;
    }
  }

  // 2. Google Drive URLs: match file ID
  if (trimmed.includes('drive.google.com') || trimmed.includes('docs.google.com') || trimmed.includes('lh3.googleusercontent.com/d/')) {
    const match = trimmed.match(/\/d\/([a-zA-Z0-9_-]+)/) || trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    if (match && match[1]) {
      return `gd:${match[1]}`;
    }
  }

  // 3. /api/product-image/{id}/{index} proxy URLs:
  if (trimmed.startsWith('/api/product-image/')) {
    return trimmed.split('?')[0];
  }

  // 4. Base64 images: match length + first 120 characters
  if (trimmed.startsWith('data:image/')) {
    return `data:${trimmed.length}:${trimmed.substring(0, 120)}`;
  }

  // 5. General HTTP/HTTPS URLs: strip query string for canonical key
  try {
    const parsed = new URL(trimmed, 'https://rheditography.net');
    return `${parsed.origin}${parsed.pathname}`.toLowerCase();
  } catch {
    return trimmed.split('?')[0].toLowerCase();
  }
}

export function getSafeProductImages(product?: { previewImages?: string[]; previewUrl?: string; image?: string; images?: string[]; category?: string; title?: string; country?: string; formats?: string[] } | null): string[] {
  if (!product) {
    return [
      generateDynamicProductSvg({ title: 'Sample Vector Document', category: 'Passport' }, 'front'),
      generateDynamicProductSvg({ title: 'Sample Vector Document', category: 'Passport' }, 'back')
    ];
  }

  // Extract from all possible image fields in Firestore documents
  const collected: string[] = [];

  const addImageCandidate = (cand: any) => {
    if (!cand) return;
    if (Array.isArray(cand)) {
      cand.forEach(item => {
        if (typeof item === 'string' && item.trim().length > 0) collected.push(item.trim());
      });
    } else if (typeof cand === 'string' && cand.trim().length > 0) {
      collected.push(cand.trim());
    }
  };

  // If previewImages is provided and has items, it is the authoritative gallery (do not resurrect old deleted images)
  if (Array.isArray(product.previewImages) && product.previewImages.length > 0) {
    addImageCandidate(product.previewImages);
  } else if (Array.isArray((product as any).images) && (product as any).images.length > 0) {
    addImageCandidate((product as any).images);
  } else if (product.image) {
    addImageCandidate(product.image);
  } else if (product.previewUrl) {
    addImageCandidate(product.previewUrl);
  }

  // Filter out any unsplash stock photos of people/statues or invalid strings or rainbow placeholders
  const validCustomImages = collected.filter(img => {
    if (!img || typeof img !== 'string' || img.length === 0) return false;
    const lower = img.toLowerCase();
    if (lower.includes('unsplash.com') || lower.includes('618005182384')) return false;
    return true;
  });

  if (validCustomImages.length > 0) {
    const normalized = validCustomImages
      .map(normalizeProductImageUrl)
      .filter(Boolean);

    // Deduplicate while preserving first-seen order using canonical keys
    const seenKeys = new Set<string>();
    const deduplicated: string[] = [];
    for (const url of normalized) {
      const clean = url.trim();
      const key = getImageDeduplicationKey(clean);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        deduplicated.push(clean);
      }
    }

    if (deduplicated.length > 0) {
      return deduplicated;
    }
  }

  // Generate title-matched dynamic SVG vector previews for admin editor/previews only
  const frontSvg = generateDynamicProductSvg(product, 'front');
  const backSvg = generateDynamicProductSvg(product, 'back');

  return [frontSvg, backSvg];
}

export const KNOWN_DELETED_IDS = [
  'rh-mutyo3w7-v9nn2', // Italy Driving License Vector Design
  'rh-msm6jl6z-lachk', // Victoria Australia Driving License Psd Template
  'rh-msnsm148-2v0sm',
  'rh-mstkj6lx-c4u7q', // Bulgaria ID Card Psd Template - Victor
  'rh-msns0mwh-fe4xp', // Bulgaria New ID Card Psd Template
  'rh-msruj39n-6u166', // Bulgaria Driving License Psd Template
  'rh-mte7tgj0-2xjdf', // Bulgaria New Identity Card - Vector Design
];

/**
 * Universal validator to check if a product has genuine, high-quality media.
 * The published store must NEVER display a product without an actual real image.
 */
export function hasProductRealImage(p: any): boolean {
  if (!p || !p.id) return false;
  if (KNOWN_DELETED_IDS.includes(p.id)) return false;
  if ((p as any).isDeleted === true || (p as any).status === 'deleted') return false;

  const img = (typeof p.image === 'string' ? p.image.trim() : '');
  const prevImgs = Array.isArray(p.previewImages) ? p.previewImages : [];
  const firstPrev = (prevImgs.length > 0 && typeof prevImgs[0] === 'string' ? prevImgs[0].trim() : '');
  const imgs = Array.isArray(p.images) ? p.images : [];
  const firstImgs = (imgs.length > 0 && typeof imgs[0] === 'string' ? imgs[0].trim() : '');

  const candidate = img || firstPrev || firstImgs || (typeof p.previewUrl === 'string' ? p.previewUrl.trim() : '');
  
  // Reject invalid placeholder strings
  if (candidate === 'Loading...' || candidate === 'null' || candidate === 'undefined') {
    return false;
  }

  // Reject known deleted product IDs embedded in image proxy URLs
  for (const delId of KNOWN_DELETED_IDS) {
    if (candidate.includes(delId)) return false;
  }

  return true;
}

export function getSafeProductFirstImage(product?: { previewImages?: string[]; previewUrl?: string; category?: string; title?: string; country?: string; formats?: string[] } | null): string {
  const images = getSafeProductImages(product);
  return images[0];
}

export function getSafeFormatProductImages(product?: any, format?: string): string[] {
  if (product && product.formatImages && format && product.formatImages[format] && Array.isArray(product.formatImages[format]) && product.formatImages[format].length > 0) {
    const customFormatImgs = product.formatImages[format];
    const mapped = customFormatImgs.map((img: string) => {
      if (!img || typeof img !== 'string') return '';
      if (img.startsWith('data:') || img.startsWith('/') || img.includes('wsrv.nl')) return img;
      return `https://wsrv.nl/?url=${encodeURIComponent(img)}&w=800&output=webp&q=85&we`;
    }).filter(Boolean);

    const seenKeys = new Set<string>();
    const deduplicated: string[] = [];
    for (const url of mapped) {
      const clean = url.trim();
      const key = getImageDeduplicationKey(clean);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        deduplicated.push(clean);
      }
    }
    if (deduplicated.length > 0) return deduplicated;
  }
  return getSafeProductImages(product);
}

